import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Link, useLocation, useNavigate } from "react-router-dom"
import {
  ChevronDown,
  LogIn,
  LogOut,
  Menu,
  Monitor,
  Moon,
  ShoppingCart,
  Sun,
  User,
  X,
} from "lucide-react"
// The source wordmark is 3264px wide and 404 kB — it was being downloaded in
// full on every page to render at ~178px. These are the same mark at 1x/2x for
// the header slot: 13 kB and 29 kB as WebP, with PNG kept as the fallback.
import logoWebp from "../assets/logos/mindalitix_tp_400.webp"
import logoWebp2x from "../assets/logos/mindalitix_tp_800.webp"
import logoPng from "../assets/logos/mindalitix_tp_400.png"
import logoPng2x from "../assets/logos/mindalitix_tp_800.png"
import { useAuth } from "../context/auth"
import { useCartStore } from "../store/cart"
import { PERMISSIONS, UserRole } from "../types.ts"
import { useCan } from "../queries/permissions"
import type { UserProfile } from "../types.ts"
import { useTheme } from "../context/ThemeContext"
import { Button, cn } from "./ui"

type ThemePreference = "light" | "dark" | "system"

interface NavChild {
  name: string
  href: string
}

interface NavItem {
  name: string
  href: string
  hasDropdown?: boolean
  dropdownItems?: NavChild[]
}

const THEME_OPTIONS: { key: ThemePreference; icon: typeof Sun; label: string }[] = [
  { key: "light", icon: Sun, label: "Light" },
  { key: "system", icon: Monitor, label: "System" },
  { key: "dark", icon: Moon, label: "Dark" },
]

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

/**
 * The mobile navigation is a real drawer rather than the desktop bar stacked
 * vertically: full-height panel, 44px rows, scroll lock, Escape and a focus trap —
 * the same contract the shared `Modal` primitive implements for dialogs.
 */
const MobileNavDrawer = ({
  open,
  onClose,
  items,
  isActive,
  resourcesOpen,
  onToggleResources,
  currentUser,
  themePreference,
  onThemeChange,
  onSignIn,
  onLogout,
}: {
  open: boolean
  onClose: () => void
  items: NavItem[]
  isActive: (path: string) => boolean
  resourcesOpen: boolean
  onToggleResources: () => void
  currentUser: UserProfile | null
  themePreference: string
  onThemeChange: (theme: ThemePreference) => void
  onSignIn: () => void
  onLogout: () => void
}) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const restoreFocusTo = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return

    restoreFocusTo.current = document.activeElement as HTMLElement | null
    const { overflow } = document.body.style
    document.body.style.overflow = "hidden"
    panelRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== "Tab" || !panelRef.current) return

      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null)
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("keydown", onKeyDown)
      document.body.style.overflow = overflow
      restoreFocusTo.current?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  const rowBase =
    "flex min-h-[44px] w-full items-center rounded-md px-4 text-[0.9375rem] font-medium transition-colors duration-200"

  return createPortal(
    <div className="fixed inset-0 z-drawer lg:hidden">
      <div
        className="absolute inset-0 scrim"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        id="mobile-navigation"
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        tabIndex={-1}
        className="absolute inset-y-0 right-0 flex w-[88%] max-w-sm flex-col border-l border-border bg-background-main shadow-lg animate-fade-up focus:outline-none"
      >
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-border px-4">
          <span className="font-sans text-caption font-semibold uppercase tracking-[0.08em] text-text-secondary">
            Menu
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation menu"
            className="flex h-11 w-11 items-center justify-center rounded-md text-text-secondary transition-colors duration-200 hover:bg-background-alt hover:text-text-primary"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
          <nav aria-label="Main">
            <ul className="space-y-1">
              {items.map((item) => (
                <li key={item.name}>
                  {item.hasDropdown ? (
                    <>
                      <button
                        type="button"
                        onClick={onToggleResources}
                        aria-expanded={resourcesOpen}
                        aria-controls={`mobile-section-${item.name}`}
                        className={cn(
                          rowBase,
                          "justify-between",
                          isActive(item.href)
                            ? "bg-primary/10 text-primary"
                            : "text-text-primary hover:bg-background-alt",
                        )}
                      >
                        {item.name}
                        <ChevronDown
                          size={16}
                          aria-hidden="true"
                          className={cn(
                            "text-muted-foreground transition-transform duration-200",
                            resourcesOpen && "rotate-180",
                          )}
                        />
                      </button>
                      {resourcesOpen && (
                        <ul
                          id={`mobile-section-${item.name}`}
                          className="mt-1 space-y-1 border-l border-border pl-3"
                        >
                          {item.dropdownItems?.map((dropItem) => (
                            <li key={dropItem.name}>
                              <Link
                                to={dropItem.href}
                                onClick={onClose}
                                className={cn(
                                  rowBase,
                                  isActive(dropItem.href)
                                    ? "text-primary"
                                    : "text-text-secondary hover:bg-background-alt hover:text-text-primary",
                                )}
                              >
                                {dropItem.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={onClose}
                      aria-current={isActive(item.href) ? "page" : undefined}
                      className={cn(
                        rowBase,
                        isActive(item.href)
                          ? "bg-primary/10 text-primary"
                          : "text-text-primary hover:bg-background-alt",
                      )}
                    >
                      {item.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="mt-6 border-t border-border pt-4">
            <p className="px-4 pb-2 text-overline font-semibold uppercase text-muted-foreground">
              Theme
            </p>
            <div className="flex gap-1 px-2">
              {THEME_OPTIONS.map(({ key, icon: Icon, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => onThemeChange(key)}
                  aria-pressed={themePreference === key}
                  className={cn(
                    "flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-md text-caption font-medium transition-colors duration-200",
                    themePreference === key
                      ? "bg-primary text-white"
                      : "text-text-secondary hover:bg-background-alt hover:text-text-primary",
                  )}
                >
                  <Icon size={16} aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="shrink-0 border-t border-border p-4">
          {currentUser?._id ? (
            <div className="space-y-3">
              <div className="px-1">
                <p className="text-caption font-semibold text-text-primary">
                  {currentUser?.displayName || currentUser.email?.split("@")[0]}
                </p>
                <p className="truncate text-caption text-muted-foreground">{currentUser.email}</p>
              </div>
              <Button
                variant="secondary"
                fullWidth
                leadingIcon={<LogOut size={16} aria-hidden="true" />}
                onClick={onLogout}
              >
                Logout
              </Button>
            </div>
          ) : (
            <Button
              fullWidth
              leadingIcon={<LogIn size={16} aria-hidden="true" />}
              onClick={onSignIn}
            >
              Sign In
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

const Header = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [resourcesDropdown, setResourcesDropdown] = useState(false)
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false)
  const [profileDropdown, setProfileDropdown] = useState(false)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { themePreference, setTheme } = useTheme()
  // `logout` must come from the context, not straight from the API module: the
  // bare API call signs the user out server-side but leaves the token, the
  // cached user and the store untouched, so the UI still looks signed in.
  const { currentUser, logout } = useAuth()
  const { can } = useCan()

  const { getTotalItems } = useCartStore()
  const totalCartItems = getTotalItems()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target?.closest(".resources-dropdown")) {
        setResourcesDropdown(false)
      }
      if (!target?.closest(".profile-dropdown")) {
        setProfileDropdown(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setResourcesDropdown(false)
        setProfileDropdown(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  // The drawer is mobile-only; growing past the lg breakpoint while it is open
  // would otherwise leave the body scroll lock in place with nothing visible.
  useEffect(() => {
    if (!isOpen) return
    const query = window.matchMedia("(min-width: 1024px)")
    const handleChange = () => {
      if (query.matches) setIsOpen(false)
    }
    handleChange()
    query.addEventListener("change", handleChange)
    return () => query.removeEventListener("change", handleChange)
  }, [isOpen])

  const handleThemeChange = (newTheme: ThemePreference) => {
    setTheme(newTheme)
    setProfileDropdown(false)
  }

  const handleLogout = async () => {
    await logout(); // Call the new logout function
    setProfileDropdown(false);
    setIsOpen(false);
    navigate("/auth");
  }

  const navItems: NavItem[] = [
    { name: "Home", href: "/" },
    { name: "Courses", href: "/courses" },
    { name: "Scholarships", href: "/scholarships" },
    { name: "Blog", href: "/blog" },
    { name: "Events", href: "/events" },
    {
      name: "Resources",
      href: "/Explore Categories",
      hasDropdown: true,
      dropdownItems: [
        { name: "About Us", href: "/about-us" },
        // "Partner programme" (earn by referring) is a different thing from
        // "Partnerships" (institutional and investment), and the two sit next to
        // each other here, so both labels have to say which is which.
        { name: "Partner Programme", href: "/partners" },
        { name: "Partnerships", href: "/partnerships" },
        { name: "Teams", href: "/teams" },
        { name: "Contact Us", href: "/contact-us" },
      ],
    },
  ]

  // Reads the router location rather than window.location so the active state
  // updates on client-side navigation instead of only on a full page load.
  const isActive = (path: string) => {
    const currentPath = pathname || "/"
    if (path === "/") {
      return currentPath === "/"
    }
    return currentPath === path || (path !== "/" && currentPath.startsWith(path))
  }

  const usableNav = currentUser?._id ? [...navItems] : navItems.filter((it) => it.name !== "Profile")
  /**
   * Asks what the account may do, not what it is called.
   *
   * This tested `role === ADMIN || role === SUPER_ADMIN`, which is the exact
   * question the permission model exists to replace. Six staff roles —
   * marketing, support, finance, admissions, contentManager, auditor, and now
   * socialMedia — hold ADMIN_PANEL and saw no link at all: the panel was built
   * for them and reachable only by typing the URL.
   *
   * It points at `/admin` rather than `/admin/dashboard` for the same reason.
   * The dashboard needs STATS_READ, so linking there sent anyone without it
   * from "no way in" to "a way in that 403s". `/admin` resolves to the first
   * section the account can actually open.
   */
  if (currentUser?._id && can(PERMISSIONS.ADMIN_PANEL)) {
    if (!usableNav.find((item) => item.name === "Admin Panel")) {
      usableNav.push({ name: "Admin Panel", href: "/admin" })
    }
  }

  if (
    currentUser?._id &&
    (currentUser?.role !== UserRole.ADMIN && currentUser.role !== UserRole.SUPER_ADMIN)
  ) {
    if (!usableNav.find((item) => item.name === "Profile")) {
      usableNav.push({ name: "Profile", href: "/profile" })
    }
  }

  // `relative` anchors the gold active underline drawn by the callers below.
  const navLinkBase =
    "relative flex h-10 items-center rounded-md px-3.5 text-caption font-medium font-sans transition-colors duration-200"

  return (
    <>
      {/* First tab stop on every page: the header renders ahead of the routed
          content, so this is the only place the skip link can lead the order. */}
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      {/* Pinned to the top with one hairline rule and an opaque surface: no blur,
          no shadow, no colour change on scroll. Kept out of flow so the page
          padding that compensates for it stays a single known value (4rem). */}
      <header className="fixed inset-x-0 top-0 z-header border-b border-border bg-background-main">
        <div className="container-editorial">
          <div className="flex h-16 items-center justify-between gap-4">
            <Link
              to="/"
              className="flex shrink-0 items-center"
              aria-label="Mindalitix Academy — home"
            >
              <picture>
                <source srcSet={`${logoWebp} 1x, ${logoWebp2x} 2x`} type="image/webp" />
                <img
                  src={logoPng}
                  srcSet={`${logoPng} 1x, ${logoPng2x} 2x`}
                  alt="Mindalitix Academy"
                  width={178}
                  height={40}
                  /* Part of the first paint — never lazy. Spread because
                     fetchPriority is valid HTML but missing from React 18 typings. */
                  {...{ fetchpriority: 'high' }}
                  className="h-9 w-auto object-contain sm:h-10"
                />
              </picture>
            </Link>

            {/* Desktop navigation */}
            <nav aria-label="Main" className="hidden lg:flex lg:items-center lg:gap-0.5">
              {usableNav.map((item) => (
                <div key={item.name} className="resources-dropdown relative">
                  {item.hasDropdown ? (
                    <div
                      onMouseEnter={() => setResourcesDropdown(true)}
                      onMouseLeave={() => setResourcesDropdown(false)}
                    >
                      <button
                        type="button"
                        onClick={() => setResourcesDropdown((prev) => !prev)}
                        aria-expanded={resourcesDropdown}
                        aria-controls={`desktop-menu-${item.name}`}
                        className={cn(
                          navLinkBase,
                          "gap-1.5",
                          isActive(item.href)
                            ? "text-text-primary after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:rounded-full after:bg-gold after:content-['']"
                            : "text-text-secondary hover:bg-background-alt hover:text-text-primary",
                        )}
                      >
                        {item.name}
                        <ChevronDown
                          size={16}
                          aria-hidden="true"
                          className={cn(
                            "transition-transform duration-200",
                            resourcesDropdown && "rotate-180",
                          )}
                        />
                      </button>

                      {resourcesDropdown && (
                        <ul
                          id={`desktop-menu-${item.name}`}
                          className="absolute left-0 top-full w-56 overflow-hidden rounded-lg border border-border bg-background-card py-1 shadow-md animate-fade-up"
                        >
                          {item.dropdownItems?.map((dropItem) => (
                            <li key={dropItem.name}>
                              <Link
                                to={dropItem.href}
                                onClick={() => setResourcesDropdown(false)}
                                className="flex min-h-[44px] items-center px-4 text-caption font-medium text-text-secondary transition-colors duration-200 hover:bg-background-alt hover:text-text-primary"
                              >
                                {dropItem.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      aria-current={isActive(item.href) ? "page" : undefined}
                      className={cn(
                        navLinkBase,
                        isActive(item.href)
                          ? "text-text-primary after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:rounded-full after:bg-gold after:content-['']"
                          : "text-text-secondary hover:bg-background-alt hover:text-text-primary",
                      )}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            <div className="flex items-center gap-1 sm:gap-2">
              <Link
                to="/cart"
                aria-label={
                  totalCartItems > 0
                    ? `Cart, ${totalCartItems} ${totalCartItems === 1 ? "item" : "items"}`
                    : "Cart"
                }
                className="relative flex h-11 w-11 items-center justify-center rounded-md text-text-secondary transition-colors duration-200 hover:bg-background-alt hover:text-text-primary"
              >
                <ShoppingCart size={20} aria-hidden="true" />
                {totalCartItems > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-sm bg-primary px-1 text-overline font-semibold leading-none text-white"
                  >
                    {totalCartItems}
                  </span>
                )}
              </Link>

              {/* Account menu / sign in — desktop */}
              {currentUser?._id ? (
                <div className="profile-dropdown relative hidden lg:block">
                  <button
                    type="button"
                    onClick={() => setProfileDropdown(!profileDropdown)}
                    aria-expanded={profileDropdown}
                    aria-controls="account-menu"
                    aria-label="Account menu"
                    className="flex h-11 items-center gap-1.5 rounded-md border border-border px-2.5 text-text-secondary transition-colors duration-200 hover:bg-background-alt hover:text-text-primary"
                  >
                    <User size={18} aria-hidden="true" className="text-primary" />
                    <ChevronDown
                      size={16}
                      aria-hidden="true"
                      className={cn("transition-transform duration-200", profileDropdown && "rotate-180")}
                    />
                  </button>

                  {profileDropdown && (
                    <div
                      id="account-menu"
                      className="absolute right-0 top-full mt-2 w-64 overflow-hidden rounded-lg border border-border bg-background-card shadow-md animate-fade-up"
                    >
                      <div className="border-b border-border px-4 py-3">
                        <p className="text-caption font-semibold text-text-primary">
                          {currentUser?.displayName || currentUser.email?.split("@")[0]}
                        </p>
                        <p className="truncate text-caption text-muted-foreground">
                          {currentUser.email}
                        </p>
                      </div>

                      <div className="py-1">
                        <button
                          type="button"
                          onClick={() => {
                            setProfileDropdown(false)
                            navigate("/profile")
                          }}
                          className="flex min-h-[44px] w-full items-center gap-3 px-4 text-caption font-medium text-text-secondary transition-colors duration-200 hover:bg-background-alt hover:text-text-primary"
                        >
                          <User size={16} aria-hidden="true" />
                          Profile
                        </button>
                      </div>

                      <div className="border-t border-border px-3 py-3">
                        <p className="mb-2 px-1 text-overline font-semibold uppercase text-muted-foreground">
                          Theme
                        </p>
                        <div className="flex gap-1">
                          {THEME_OPTIONS.map(({ key, icon: Icon, label }) => (
                            <button
                              key={key}
                              type="button"
                              onClick={() => handleThemeChange(key)}
                              aria-pressed={themePreference === key}
                              className={cn(
                                "flex flex-1 items-center justify-center gap-1 rounded-md py-2 text-caption font-medium transition-colors duration-200",
                                themePreference === key
                                  ? "bg-primary text-white"
                                  : "text-text-secondary hover:bg-background-alt hover:text-text-primary",
                              )}
                            >
                              <Icon size={14} aria-hidden="true" />
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="border-t border-border py-1">
                        <button
                          type="button"
                          onClick={handleLogout}
                          className="flex min-h-[44px] w-full items-center gap-3 px-4 text-caption font-medium text-error transition-colors duration-200 hover:bg-error/10"
                        >
                          <LogOut size={16} aria-hidden="true" />
                          Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="hidden lg:block">
                  <Button onClick={() => navigate("/auth")}>Sign In</Button>
                </div>
              )}

              {/* Mobile menu toggle */}
              <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                aria-controls="mobile-navigation"
                aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
                className="flex h-11 w-11 items-center justify-center rounded-md text-text-primary transition-colors duration-200 hover:bg-background-alt lg:hidden"
              >
                <Menu size={20} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <MobileNavDrawer
        open={isOpen}
        onClose={() => setIsOpen(false)}
        items={usableNav}
        isActive={isActive}
        resourcesOpen={mobileResourcesOpen}
        onToggleResources={() => setMobileResourcesOpen(!mobileResourcesOpen)}
        currentUser={currentUser}
        themePreference={themePreference}
        onThemeChange={setTheme}
        onSignIn={() => {
          setIsOpen(false)
          navigate("/auth")
        }}
        onLogout={handleLogout}
      />
    </>
  )
}

export default Header
